/**
 * Wires the room token refresher (roomTokenRefresh.ts) to the connection:
 * it starts when a connection opens and stops when it closes or fails. A
 * failed connection is the reload path's job, which gets its own new token.
 * Leaving a conference alone does not stop it: a restarted conference can
 * join again on the same connection.
 */
import { CONNECTION_DISCONNECTED, CONNECTION_ESTABLISHED, CONNECTION_FAILED } from '../base/connection/actionTypes';
import { getLogger } from '../base/logging/functions';
import MiddlewareRegistry from '../base/redux/MiddlewareRegistry';
import { addJitsiRoomToken } from './jitsiRoomToken';
import { createRoomTokenRefresher } from './roomTokenRefresh';
const logger = getLogger('features/vrs-auth');
/**
 * Gets a new token for the room the same way a join does (account, or the
 * guest grant in the URL), from the room URL without its old token.
 *
 * @param {URL|undefined} locationURL - The room URL.
 * @param {string} roomName - The room.
 * @returns {Promise<string>}
 */
async function mintFromLocation(locationURL, roomName) {
    if (!locationURL) {
        throw new Error('No room URL');
    }
    const base = new URL(locationURL.toString());
    base.searchParams.delete('jwt');
    base.searchParams.delete('moveCall');
    const token = (await addJitsiRoomToken(base, roomName)).searchParams.get('jwt');
    if (!token) {
        throw new Error('No room token');
    }
    return token;
}
MiddlewareRegistry.register(store => {
    const refresher = createRoomTokenRefresher({
        clearTimeout: handle => clearTimeout(handle),
        currentConnection: () => store.getState()['features/base/connection'].connection,
        log: (event, details) => logger.info(event, details ?? {}),
        mint: roomName => mintFromLocation(store.getState()['features/base/connection'].locationURL, roomName),
        now: () => Date.now(),
        setTimeout: (callback, ms) => setTimeout(callback, ms)
    });
    return next => action => {
        const result = next(action);
        switch (action.type) {
            case CONNECTION_ESTABLISHED:
                refresher.schedule(action.connection, store.getState()['features/base/conference'].room);
                break;
            case CONNECTION_DISCONNECTED:
            case CONNECTION_FAILED:
                refresher.stop(action.connection);
                break;
        }
        return result;
    };
});
